import React from 'react';
import Button from '@mui/material/Button';
import { Tooltip } from '@mui/material';

import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { makeStyles } from 'src/makeStyles';
import { TP, PRODUCTION_VIEW_TYPES } from '../../../constants/consts';
import { useTranslation } from '../../../src/i18n';
import { colors, fontSize } from '../../../constants/styleConstants';
import DateFilter from './dateFilter';

const useStyles = makeStyles()(() => ({
  content: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
    color: colors.textColor,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
  },
  selected: {
    borderRadius: '8px',
    border: `solid 1px ${colors.blue3}`,
    backgroundColor: colors.blue1,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
    color: colors.blue3,
    fontWeight: 'bold',
  },
  selectedValue: {
    border: `solid 1px ${colors.blue3}`,
    backgroundColor: colors.blue1,
  },
  icon: {
    color: colors.blue3,
    fontSize: fontSize.large,
  },
  iconClose: {
    color: colors.blue3,
    fontSize: fontSize.medium,
  },
  disabledIcon: {
    color: 'rgba(0, 0, 0, 0.26)',
  },
  customStyle: {
    textTransform: 'none',
    fontSize: fontSize.smallTitle,
    margin: '2px 4px 2px 0px',
    marginRight: '8px',
  },
  label: {
    marginLeft: '8px',
  },
  popper: {
    maxWidth: '400px',
    maxHeight: '300px',
    zIndex: '998',
    border: `solid 1px ${colors.grey}`,
  },
  selectedItem: {
    backgroundColor: `${colors.blue1} !important`,
  },
  sortBox: {
    marginLeft: 'auto',
  },
  sortLabel: {
    paddingRight: '10px',
    fontWeight: 'bold',
    color: colors.gray,
  },
}));

export default function FBDropdownButton({
  label,
  state,
  dispatch,
  type,
  onSubmit,
  options,
  getSelectedValue,
  isSmall,
  tabViewType,
  seasonsOptions,
  viewType,
  defaultSort,
  isStubPage,
  page,
  openDropdown,
  apiTriggered,
}) {
  const selectedValue = getSelectedValue(state, type);
  const { classes, cx } = useStyles();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [selectedIndex, setSelectedIndex] = React.useState(selectedValue || -1);
  const { t } = useTranslation();
  const dateSort = defaultSort ? '-year' : 'year';

  React.useEffect(() => {
    setSelectedIndex(selectedValue || -1);
  }, [options, isSmall, selectedValue]);

  const resetFilter = filterType => {
    if (
      filterType === 'language_id' ||
      filterType === 'subtitle_id' ||
      filterType === 'country_id' ||
      filterType === 'year_id' ||
      filterType === 'season_id' ||
      filterType === 'sort'
    ) {
      dispatch({
        type: 'RESET_FILTERS',
        payload: filterType,
      });
    }
  };

  const handleMenuItemClick = (filterType, index, value) => {
    setSelectedIndex(value);
    if (value == -1) resetFilter(filterType);
    onSubmit && onSubmit({ filterType, selectedValue: value != -1 ? options[index] : null });
    setOpen(false);
  };

  const handleToggle = () => {
    if (!open) {
      openDropdown();
    }
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };
  let showFilter = true;
  if (tabViewType === 'upcoming' && type === 'sort') showFilter = false;

  const toolTipContent = tooltipType => {
    switch (tooltipType) {
      case 'language_id':
        return t(`${TP}.FN_LANGUAGE_TOOLTIP`);
      case 'subtitle_id':
        return t(`${TP}.FN_SURTITLES_TOOLTIP`);
      default:
        return '';
    }
  };

  const menuOptions = (
    <MenuList id="split-button-menu">
      {options?.length > 0 ? (
        options.map((option, index) => (
          <MenuItem
            key={option.id}
            className={option.id === selectedIndex ? classes.selectedItem : ''}
            selected={option.id === selectedIndex}
            onClick={() => handleMenuItemClick(type, index, option.id)}
          >
            {t(option.name)}
          </MenuItem>
        ))
      ) : !apiTriggered ? (
        <MenuItem>Loading...</MenuItem>
      ) : (
        <MenuItem>No options available</MenuItem>
      )}
    </MenuList>
  );

  const isDisabled = type === 'date' && tabViewType === 'upcoming';
  const content = (
    <>
      {showFilter && (
        <Tooltip title={toolTipContent(type)} placement="top">
          <div className={type === 'sort' && classes.sortBox}>
            {type === 'sort' && <span className={classes.sortLabel}> {t(`${TP}.FN_SORTBY`)}</span>}
            <Button
              size="small"
              aria-expanded={open ? 'true' : undefined}
              aria-label={label}
              aria-haspopup="menu"
              onClick={handleToggle}
              disabled={isDisabled}
              className={cx(open ? classes.selected : classes.content, classes.customStyle, {
                [classes.selectedValue]: selectedIndex !== -1,
              })}
              ref={anchorRef}
            >
              {options?.length > 0 ? (
                <span className={classes.label}> {t(options?.filter(item => item.id == selectedIndex)[0]?.name)}</span>
              ) : (
                <span className={classes.label}> {label}</span>
              )}
              {selectedIndex === -1 || (type === 'year_id' && page !== 'videos') ? (
                <ExpandMore className={cx(classes.icon, isDisabled && classes.disabledIcon)} />
              ) : (
                <CloseIcon
                  className={classes.iconClose}
                  onClick={e => {
                    e.stopPropagation();
                    let defaultValue = -1;
                    let defaultIndex = 0;
                    if (type === 'sort') {
                      const hasValue =
                        options.filter(item => item.id === dateSort || item.id === 'work.name').length > 0;
                      if (hasValue) {
                        defaultValue = PRODUCTION_VIEW_TYPES.PRODUCTION === viewType ? dateSort : options[0].id;
                        defaultIndex =
                          PRODUCTION_VIEW_TYPES.PRODUCTION === viewType
                            ? options.findIndex(item => item.id === dateSort)
                            : 0;
                      } else {
                        defaultValue = options[0].id;
                      }
                    }

                    handleMenuItemClick(type, defaultIndex, defaultValue);
                  }}
                />
              )}
            </Button>
          </div>
        </Tooltip>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        className={classes.popper}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              {type === 'date' ? (
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu">
                    <DateFilter
                      state={{ ...state }}
                      dispatch={dispatch}
                      onSubmit={onSubmit}
                      handleToggle={handleToggle}
                      seasonsOptions={seasonsOptions}
                      isStubPage={isStubPage}
                    />
                  </MenuList>
                </ClickAwayListener>
              ) : (
                menuOptions && <ClickAwayListener onClickAway={handleClose}>{menuOptions}</ClickAwayListener>
              )}
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
  const withTooltip = (
    <Tooltip title={isDisabled && t(`${TP}.FN_DISABLE_SUBFILTER_TOOLTIP`)}>
      <span>{content}</span>
    </Tooltip>
  );

  return isDisabled ? withTooltip : content;
}
